import React from 'react';

export default function ImportWizardHelp() {
  return (
    <div className="modal-content">
      <div className="modal-header">
        <button type="button" className="close" data-dismiss="modal">
          <span>×</span>
        </button>
        <h4 className="modal-title">Import Wizard - Complete Guide</h4>
      </div>
      <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <div className="help-content">

          <section>
            <h3>Overview</h3>
            <p>
              The Import Wizard converts existing websites into Dashboard Online templates using AI-assisted analysis.
              This process follows the documented conversion methodology to create proper Base Templates and Components.
            </p>
          </section>

          <section>
            <h3>Step 1: Fetch Website</h3>
            <h5>What it does:</h5>
            <ul>
              <li>Fetches the HTML content from the URL you provide</li>
              <li>Removes scripts and iframes for security</li>
              <li>Automatically advances to AI analysis</li>
            </ul>
            <h5>How to use:</h5>
            <ol>
              <li>Enter the complete URL (must start with http:// or https://)</li>
              <li>Click <strong>"Fetch & Analyze"</strong></li>
              <li>Wait for the page to load and analysis to begin</li>
            </ol>
            <div className="alert alert-info">
              <strong>Tip:</strong> Use clean, well-structured pages for best results. Landing pages and marketing sites work well.
            </div>
          </section>

          <section>
            <h3>Step 2: AI Analysis & Extraction</h3>
            <h5>What happens automatically:</h5>
            <ul>
              <li><strong>AI reads the conversion guide</strong> to understand Dashboard template structure</li>
              <li><strong>Identifies semantic regions:</strong>
                <ul>
                  <li>Header (navigation, branding)</li>
                  <li>Main content (body)</li>
                  <li>Footer (copyright, links)</li>
                </ul>
              </li>
              <li><strong>Extracts all CSS</strong> including fonts, icons, and styling</li>
              <li><strong>Creates template preview</strong> showing header + {'{'}{'{'} content {'}'}{'}'}  + footer</li>
            </ul>
            <div className="alert alert-warning">
              <strong>Note:</strong> If AI cannot detect clear regions, try a different page or one with semantic HTML5 tags (&lt;header&gt;, &lt;main&gt;, &lt;footer&gt;).
            </div>
          </section>

          <section>
            <h3>Step 3: Template Preview & Components</h3>

            <h4>Template Preview Panel</h4>
            <p>Shows how your template will look with the extracted structure:</p>
            <ul>
              <li><strong>Header section</strong> - Site navigation and branding</li>
              <li><strong>Content placeholder</strong> - Gray box showing where {'{'}{'{'} content {'}'}{'}'}  will be injected</li>
              <li><strong>Footer section</strong> - Bottom of page</li>
              <li><strong>Isolated CSS</strong> - Template renders with its own styling, not Dashboard's</li>
            </ul>

            <h4>Creating Components</h4>
            <p>Components are reusable sections you can embed in multiple pages using {'{'}{'{'} COMP:tid_name {'}'}{'}'} syntax.</p>

            <h5>To create a component:</h5>
            <ol>
              <li>Click <strong>"Select Components"</strong> button</li>
              <li><strong>Hover over elements</strong> in the preview (blue outline appears)</li>
              <li><strong>Click to select</strong> (green outline confirms selection)</li>
              <li>Select multiple elements if needed (they'll be combined)</li>
              <li>Fill in the component form:
                <ul>
                  <li><strong>Title:</strong> Descriptive name (e.g. "Main Navigation")</li>
                  <li><strong>TID:</strong> Auto-generated in snake_case (e.g. "main_navigation"), editable</li>
                  <li><strong>Description:</strong> Optional notes about the component</li>
                </ul>
              </li>
              <li>Click <strong>"Save Component"</strong></li>
            </ol>

            <div className="alert alert-success">
              <strong>Good component examples:</strong>
              <ul>
                <li>Hero sections</li>
                <li>Call-to-action blocks</li>
                <li>Testimonial sections</li>
                <li>Feature grids</li>
                <li>Newsletter signup forms</li>
              </ul>
            </div>

            <h5>Component Tokens</h5>
            <p>After saving, components can be used with <code>{'{'}{'{'} COMP:your_tid {'}'}{'}'}</code> syntax:</p>
            <pre><code>{`<div class="hero-section">
  {{COMP:hero_banner}}
</div>`}</code></pre>
          </section>

          <section>
            <h3>Step 4: Save Template</h3>
            <h5>Final template creation:</h5>
            <ul>
              <li><strong>Title:</strong> Human-readable name for the template</li>
              <li><strong>TID:</strong> Unique identifier (auto-generated from title, editable)</li>
              <li><strong>Description:</strong> Optional notes about when to use this template</li>
            </ul>

            <h5>What gets saved:</h5>
            <ul>
              <li>Base Template HTML (header + {'{'}{'{'} content {'}'}{'}'}  + footer)</li>
              <li>All extracted CSS</li>
              <li>Component tokens replacing original HTML</li>
              <li>Template metadata (title, TID, description)</li>
            </ul>

            <div className="alert alert-info">
              <strong>After saving:</strong> You can assign this template to pages. The template wraps page content, and components can be embedded anywhere using their tokens.
            </div>
          </section>

          <section>
            <h3>Template Structure Explained</h3>
            <p>Dashboard templates follow a specific pattern:</p>

            <h5>Base Template:</h5>
            <pre><code>{`<header>
  <!-- Navigation, branding -->
</header>

{{content}}

<footer>
  <!-- Copyright, links -->
</footer>`}</code></pre>

            <h5>Page Content (stored separately):</h5>
            <pre><code>{`<div class="container">
  <h1>Welcome</h1>
  {{COMP:hero_banner}}
  <p>Page content here...</p>
</div>`}</code></pre>

            <h5>Final Rendered Page:</h5>
            <p>Dashboard merges them:</p>
            <pre><code>{`<header><!-- Navigation --></header>
<div class="container">
  <h1>Welcome</h1>
  <!-- Hero banner component HTML -->
  <p>Page content here...</p>
</div>
<footer><!-- Footer --></footer>`}</code></pre>
          </section>

          <section>
            <h3>Best Practices</h3>
            <ul>
              <li><strong>Start with clean pages:</strong> Marketing sites and landing pages convert best</li>
              <li><strong>Use semantic HTML:</strong> Pages with &lt;header&gt;, &lt;main&gt;, &lt;footer&gt; tags are detected more accurately</li>
              <li><strong>Create focused components:</strong> Extract sections you'll reuse (heros, CTAs, testimonials)</li>
              <li><strong>Descriptive TIDs:</strong> Use clear names like "main_nav", "footer_cta", "testimonial_grid"</li>
              <li><strong>Test the preview:</strong> Verify the template looks correct before saving</li>
              <li><strong>One template per site design:</strong> Different visual styles = different templates</li>
            </ul>
          </section>

          <section>
            <h3>Common Issues</h3>

            <h5>AI can't detect regions:</h5>
            <ul>
              <li>Try a page with clearer HTML structure</li>
              <li>Look for pages with semantic tags (&lt;header&gt;, &lt;main&gt;, &lt;footer&gt;)</li>
              <li>Avoid complex single-page apps (SPAs) - static sites work best</li>
            </ul>

            <h5>Styling looks broken:</h5>
            <ul>
              <li>Some sites use absolute URLs for assets - these won't load from different domains</li>
              <li>External CSS files need to be accessible</li>
              <li>Check console for loading errors</li>
            </ul>

            <h5>Components not appearing:</h5>
            <ul>
              <li>Ensure you clicked <strong>"Save Component"</strong> before moving to next step</li>
              <li>Check "Saved Components" list to verify</li>
            </ul>
          </section>

          <section>
            <h3>Advanced: Token Reference</h3>
            <p>Templates and pages support various tokens:</p>
            <table className="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>Token</th>
                  <th>Purpose</th>
                  <th>Example</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>{'{'}{'{'} content {'}'}{'}'}</code></td>
                  <td>Page content injection point</td>
                  <td>Required in Base Templates</td>
                </tr>
                <tr>
                  <td><code>{'{'}{'{'} COMP:tid {'}'}{'}'}</code></td>
                  <td>Embed a component</td>
                  <td><code>{'{'}{'{'} COMP:hero_banner {'}'}{'}'}</code></td>
                </tr>
                <tr>
                  <td><code>{'{'}{'{'} PAGE:tid {'}'}{'}'}</code></td>
                  <td>Embed another page</td>
                  <td><code>{'{'}{'{'} PAGE:about_us {'}'}{'}'}</code></td>
                </tr>
                <tr>
                  <td><code>{'{'}{'{'} FIELD:name {'}'}{'}'}</code></td>
                  <td>Dynamic field merging</td>
                  <td><code>{'{'}{'{'} FIELD:user_name {'}'}{'}'}</code></td>
                </tr>
              </tbody>
            </table>
          </section>

          <section>
            <h3>Workflow Summary</h3>
            <div className="well">
              <ol style={{ marginBottom: 0 }}>
                <li><strong>Fetch:</strong> Enter URL, click "Fetch & Analyze"</li>
                <li><strong>AI extracts:</strong> Header, main content, footer, CSS automatically</li>
                <li><strong>Review preview:</strong> See template structure</li>
                <li><strong>Select components:</strong> Mark reusable sections (optional but recommended)</li>
                <li><strong>Save template:</strong> Enter title/description, click "Save Template"</li>
                <li><strong>Use it:</strong> Assign template to pages, embed components with tokens</li>
              </ol>
            </div>
          </section>

        </div>
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-default" data-dismiss="modal">Close</button>
      </div>
    </div>
  );
}
